import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from '@/axios'
import config from '@/constant/config'
import { toResponse } from '@/utils/common'
import {
  mergeAdminMessages,
  mergeAdminQueueMeta,
  mergeClosedAdminConversation
} from './chat-inbox-state'

const { chat } = config.api

const request = async (callback, rejectWithValue) => {
  try {
    return (await callback()).data
  } catch (error) {
    return rejectWithValue(toResponse(error.response))
  }
}

const csrfHeaders = getState => ({
  headers: { 'X-Chat-CSRF-Token': getState().chatInbox.csrfToken }
})

export const getAdminChatConfig = createAsyncThunk(
  'chatInbox/config',
  async (_, { rejectWithValue }) => request(() => axios.get(chat.config), rejectWithValue)
)

export const updateStorefrontChatEnabled = createAsyncThunk(
  'chatInbox/config/update',
  async (enabled, { rejectWithValue, getState }) => request(
    () => axios.put(chat.config, { enabled }, csrfHeaders(getState)),
    rejectWithValue
  )
)

export const getAdminChatQueue = createAsyncThunk(
  'chatInbox/queue',
  async ({ status, scope, page = 1, size = 20 } = {}, { rejectWithValue }) => request(
    () => axios.get(chat.conversations, {
      params: { page, size, ...(status && { status }), ...(scope && { scope }) }
    }),
    rejectWithValue
  )
)

export const getAdminChatConversation = createAsyncThunk(
  'chatInbox/conversation',
  async (id, { rejectWithValue }) => request(() => axios.get(chat.conversation(id)), rejectWithValue)
)

export const sendAdminChatMessage = createAsyncThunk(
  'chatInbox/send',
  async ({ conversationId, ...body }, { rejectWithValue, getState }) => request(
    () => axios.post(chat.messages(conversationId), body, csrfHeaders(getState)),
    rejectWithValue
  )
)

export const closeAdminChatConversation = createAsyncThunk(
  'chatInbox/close',
  async (conversationId, { rejectWithValue, getState }) => request(
    () => axios.post(chat.close(conversationId), undefined, csrfHeaders(getState)),
    rejectWithValue
  )
)

export const retryChatTranscriptEmail = createAsyncThunk(
  'chatInbox/transcriptEmail/retry',
  async (conversationId, { rejectWithValue, getState }) => request(
    () => axios.post(chat.retryTranscriptEmail(conversationId), undefined, csrfHeaders(getState)),
    rejectWithValue
  )
)

export const getChatBudget = createAsyncThunk(
  'chatInbox/budget',
  async (_, { rejectWithValue }) => request(() => axios.get(chat.budget), rejectWithValue)
)

export const openChatBudget = createAsyncThunk(
  'chatInbox/budget/open',
  async (reason, { rejectWithValue, getState }) => request(
    () => axios.post(chat.openBudget, { reason }, csrfHeaders(getState)),
    rejectWithValue
  )
)

export const resetChatBudget = createAsyncThunk(
  'chatInbox/budget/reset',
  async (_, { rejectWithValue, getState }) => request(
    () => axios.post(chat.resetBudget, undefined, csrfHeaders(getState)),
    rejectWithValue
  )
)

const initialState = {
  configLoaded: false,
  configAvailable: false,
  enabled: false,
  savingConfig: false,
  liveEnabled: false,
  realtimeEnabled: false,
  timeoutEnabled: false,
  transcriptEmailEnabled: false,
  csrfToken: '',
  conversations: [],
  selectedConversation: null,
  messages: [],
  budget: null,
  counts: {
    WAITING_ADMIN: 0,
    LIVE: 0,
    CLOSED: 0,
    TIMED_OUT: 0
  },
  paging: {
    page: 1,
    size: 20,
    totalPage: 0,
    totalItem: 0
  },
  queueLoaded: false,
  loading: false,
  sending: false,
  retryingTranscriptEmail: false
}

const slice = createSlice({
  name: 'chatInbox',
  initialState,
  reducers: {
    receiveAdminQueueMeta(state, action) {
      const metaById = new Map((action.payload || []).map(item => [item.id, item]))
      state.conversations = mergeAdminQueueMeta(state.conversations, action.payload)
      if (state.selectedConversation && metaById.has(state.selectedConversation.id)) {
        state.selectedConversation = {
          ...state.selectedConversation,
          ...metaById.get(state.selectedConversation.id)
        }
      }
    },
    receiveAdminRealtimeMessages(state, action) {
      state.messages = mergeAdminMessages(state.messages, action.payload || [])
    },
    clearSelectedConversation(state) {
      state.selectedConversation = null
      state.messages = []
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getAdminChatConfig.fulfilled, (state, action) => {
        state.configLoaded = true
        state.configAvailable = true
        state.enabled = action.payload.data.enabled
        state.liveEnabled = action.payload.data.liveEnabled
        state.realtimeEnabled = action.payload.data.realtimeEnabled
        state.timeoutEnabled = action.payload.data.timeoutEnabled
        state.transcriptEmailEnabled = action.payload.data.transcriptEmailEnabled
        state.csrfToken = action.payload.data.csrfToken
      })
      .addCase(getAdminChatConfig.rejected, state => {
        state.configLoaded = true
        state.configAvailable = false
        state.liveEnabled = false
      })
      .addCase(updateStorefrontChatEnabled.pending, state => { state.savingConfig = true })
      .addCase(updateStorefrontChatEnabled.fulfilled, (state, action) => {
        state.savingConfig = false
        state.enabled = action.payload.data.enabled
      })
      .addCase(updateStorefrontChatEnabled.rejected, state => { state.savingConfig = false })
      .addCase(getAdminChatQueue.pending, state => { state.loading = !state.queueLoaded })
      .addCase(getAdminChatQueue.fulfilled, (state, action) => {
        state.loading = false
        state.queueLoaded = true
        state.conversations = action.payload.data
        if (state.selectedConversation) {
          const refreshed = action.payload.data.find(item => item.id === state.selectedConversation.id)
          if (refreshed) state.selectedConversation = { ...state.selectedConversation, ...refreshed }
        }
        state.counts = action.payload.counts
        state.paging = action.payload.paging
      })
      .addCase(getAdminChatQueue.rejected, state => {
        state.loading = false
        state.queueLoaded = true
      })
      .addCase(getAdminChatConversation.fulfilled, (state, action) => {
        state.selectedConversation = action.payload.data.conversation
        state.messages = action.payload.data.messages
        const item = state.conversations.find(value => value.id === action.payload.data.conversation.id)
        if (item) item.unreadCount = 0
      })
      .addCase(sendAdminChatMessage.pending, state => { state.sending = true })
      .addCase(sendAdminChatMessage.fulfilled, (state, action) => {
        state.sending = false
        state.selectedConversation = action.payload.data.conversation
        state.messages = mergeAdminMessages(state.messages, [action.payload.data.message])
      })
      .addCase(sendAdminChatMessage.rejected, state => { state.sending = false })
      .addCase(closeAdminChatConversation.fulfilled, (state, action) => {
        const closedConversation = action.payload.data
        state.conversations = mergeClosedAdminConversation(state.conversations, closedConversation)
        if (state.selectedConversation?.id === closedConversation.id) {
          state.selectedConversation = { ...state.selectedConversation, ...closedConversation }
        }
      })
      .addCase(retryChatTranscriptEmail.pending, state => { state.retryingTranscriptEmail = true })
      .addCase(retryChatTranscriptEmail.fulfilled, (state, action) => {
        state.retryingTranscriptEmail = false
        if (state.selectedConversation) {
          state.selectedConversation.transcriptEmail = action.payload.data
          const item = state.conversations.find(value => value.id === state.selectedConversation.id)
          if (item) item.transcriptEmail = action.payload.data
        }
      })
      .addCase(retryChatTranscriptEmail.rejected, state => { state.retryingTranscriptEmail = false })
      .addCase(getChatBudget.fulfilled, (state, action) => { state.budget = action.payload.data })
      .addCase(openChatBudget.fulfilled, (state, action) => { state.budget = action.payload.data })
      .addCase(resetChatBudget.fulfilled, (state, action) => { state.budget = action.payload.data })
  }
})

export const chatInboxSelector = state => state.chatInbox
export const {
  clearSelectedConversation,
  receiveAdminQueueMeta,
  receiveAdminRealtimeMessages
} = slice.actions
export default slice.reducer
